import { Injectable, OnDestroy } from '@angular/core';
import { interval, fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, throttleTime } from 'rxjs/operators';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { createUserStatusRecord, updateUserStatusRecord } from 'src/graphql/mutations';
import { userStatusRecordByProfile } from 'src/graphql/queries';

@Injectable({
  providedIn: 'root'
})
export class OnlineStatusService implements OnDestroy {
  private readonly ACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
  private readonly UPDATE_INTERVAL = 60 * 1000; // Update backend every 60 seconds
  
  private currentUserId: string | null = null;
  private currentProfileId: string | null = null;
  private userStatusRecordId: string | null = null;
  private lastLocalActivity: number = 0;
  private activitySubscription: Subscription | null = null;
  private updateSubscription: Subscription | null = null;
  private client = generateClient();

  private isInitialized = false;

  constructor() {
    // Don't auto-initialize - wait for manual initialization after auth
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private cleanup() {
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
      this.updateSubscription = null;
    }
    this.isInitialized = false;
  }

  // Public method to initialize the service after authentication
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    await this.initializeService();
  }

  // Public method to stop tracking (useful for logout)
  public stop(): void {
    this.cleanup();
  }

  private async initializeService() {
    try {
      // Get current user
      const user = await getCurrentUser();
      this.currentUserId = user.userId;
      
      // Get profile ID from cognito ID
      await this.loadCurrentProfile();
      
      // Find or create userStatusRecord
      await this.initializeUserStatusRecord();
      
      // Track current user's activity
      this.trackUserActivity();
      
      // Update activity immediately
      await this.updateLastActivity();
      
      // Start periodic updates
      this.startPeriodicUpdates();

      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing online status service:', error);
      throw error;
    }
  }

  private async loadCurrentProfile() {
    try {
      const session = await fetchAuthSession();
      const idTokenPayload = session.tokens?.idToken?.payload;

      if (idTokenPayload && idTokenPayload["custom:profileId"]) {
        this.currentProfileId = idTokenPayload["custom:profileId"].toString();
      } else {
        console.warn('No profileId found in session token');
      }
    } catch (error) {
      console.error('Error loading profile ID from session:', error);
    }
  }

  private async initializeUserStatusRecord() {
    if (!this.currentProfileId) {
      console.warn('No profile ID available for initializing user status record');
      return;
    }

    try {
      // First, try to find existing userStatusRecord
      const response = await this.client.graphql({
        query: userStatusRecordByProfile,
        variables: {
          profileID: this.currentProfileId
        }
      });

      const existingRecords = response.data?.userStatusRecordByProfile?.items;
      
      if (existingRecords && existingRecords.length > 0) {
        // Use existing record
        this.userStatusRecordId = existingRecords[0].id;
      } else {
        // Create new record
        const createResponse = await this.client.graphql({
          query: createUserStatusRecord,
          variables: {
            input: {
              profileID: this.currentProfileId,
              lastActivity: new Date().toISOString(),
              isOnline: true,
              lastSeen: new Date().toISOString()
            }
          }
        });

        this.userStatusRecordId = createResponse.data?.createUserStatusRecord?.id;
      }
    } catch (error) {
      console.error('Error initializing user status record:', error);
    }
  }

  private trackUserActivity() {
    if (!this.currentUserId) return;

    // Track various user activities
    const activities$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'click'),
      fromEvent(document, 'scroll'),
      fromEvent(window, 'focus')
    ).pipe(
      throttleTime(5000) // Throttle to avoid too frequent local updates
    );

    this.activitySubscription = activities$.subscribe(() => {
      this.lastLocalActivity = Date.now();
    });

    // Set initial activity
    this.lastLocalActivity = Date.now();
  }

  private startPeriodicUpdates() {
    // Update backend periodically if there was recent activity
    this.updateSubscription = interval(this.UPDATE_INTERVAL).subscribe(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastLocalActivity;
      
      // Only update if there was activity in the last update interval
      if (timeSinceLastActivity < this.UPDATE_INTERVAL) {
        this.updateLastActivity();
      }
    });
  }

  private async updateLastActivity() {
    if (!this.userStatusRecordId) {
      console.warn('No userStatusRecord ID available for updating last activity');
      return;
    }

    try {
      const now = new Date().toISOString();
      
      await this.client.graphql({
        query: updateUserStatusRecord,
        variables: {
          input: {
            id: this.userStatusRecordId,
            lastActivity: now,
            isOnline: true,
            lastSeen: now
          }
        }
      });

    } catch (error) {
      console.error('Error updating last activity:', error);
    }
  }

  // Public method to check if a user is online based on their lastActivity
  public isUserOnline(lastActivity: string | null | undefined): boolean {
    if (!lastActivity) return false;
    
    const now = Date.now();
    const activityTime = new Date(lastActivity).getTime();
    return (now - activityTime) < this.ACTIVITY_TIMEOUT;
  }

  // Get formatted last activity time
  public getLastActivityTime(lastActivity: string | null | undefined): Date | null {
    if (!lastActivity) return null;
    return new Date(lastActivity);
  }

  // Format relative time for display
  public getRelativeTime(lastActivity: string | null | undefined): string {
    if (!lastActivity) return 'Jamais connecté';

    const now = new Date();
    const activityTime = new Date(lastActivity);
    const diff = now.getTime() - activityTime.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (this.isUserOnline(lastActivity)) {
      return 'En ligne';
    } else if (days > 0) {
      return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
      return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      return 'À l\'instant';
    }
  }

  // Force update of current user's activity
  public async forceUpdateActivity() {
    await this.updateLastActivity();
  }
}